/* eslint-disable react/jsx-no-target-blank */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormFile, FormInput, FormError, Checkbox } from '@getro/rombo';
import { Field, Form, Formik } from 'formik';
import { Flex, Text, Box } from 'rebass/styled-components';
import * as Yup from 'yup';
import getConfig from 'next/config';
import { get } from '../../api/index';
import { linkedinProfileRegex, urlRegex } from '../../helpers/regex';
import { NetworkSchema } from '../../schema/network';
import SentryAPI from '../../api/SentryAPI';
import usePrivacyPolicyUrl from '../../hooks/usePrivacyPolicyUrl';
const { publicRuntimeConfig } = getConfig();

const formSchema = Yup.object().shape({
  email: Yup.string().email().required('Email is required'),
  linkedinUrl: Yup.string().matches(linkedinProfileRegex, 'Linkedin URL should be a valid URL').required(),
  additionalInfo: Yup.string().matches(urlRegex, 'Online presence should be a valid URL'),
  resumeUrl: Yup.string().nullable(),
});

export const Profile = ({ network, onChange, data, handleFileTooBig, variant }) => {
  const { apiV2BaseUrl: baseURL } = publicRuntimeConfig;
  const [resumeError, setResumeError] = useState(false);

  const isValidFilename = (filename) => {
    const regex = /^[a-zA-Z0-9-_. ]{1,255}$/;
    return regex.test(filename);
  };

  const isFileValid = (file) => {
    const isNotValidFile =
      !/^application\/(pdf|msword|vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/.test(file.type);

    if (file.size / 1024 > 10000 || isNotValidFile) {
      handleFileTooBig();
      return false;
    }

    if (!isValidFilename(file.name)) {
      return false;
    }

    return true;
  };
  const { privacyPolicyUrl } = usePrivacyPolicyUrl(network);

  const uploadFileToS3 = async (file, presignedUrl) => {
    try {
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      if (uploadResponse.statusText !== 'OK') {
        SentryAPI.trackError('Failed to upload file to S3', {
          server: true,
          networkId: network?.id || undefined,
          filename: file.name,
        });
      }
    } catch (error) {
      SentryAPI.trackError('Failed to upload file to S3', {
        server: true,
        networkId: network?.id || undefined,
        filename: file.name || undefined,
        error,
      });
    }
  };

  const handleFileChange = async (e, setFieldValue) => {
    const file = e.target.files[0];
    const fileNotValid = !isFileValid(file);

    if (fileNotValid) {
      setResumeError(true);
      e.stopPropagation();
      return;
    }

    setResumeError(false);

    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      const response = await get(
        `/collections/${network.id}/job_seekers_resume?resume_file_name=${file.name}`,
        { baseURL },
        headers,
      );

      if (response.data.presignedUrl) {
        await uploadFileToS3(file, response.data.presignedUrl);
        onChange('profile', {
          ...data,
          cvUrl: {
            name: file.name,
          },
        });
        setFieldValue('resumeUrl', response.data.resumeUrl);
      } else {
        SentryAPI.trackError('Error getting presigned URL', {
          server: true,
          networkId: network?.id || undefined,
          responseErrors: response.errors || undefined,
          e,
        });
      }
    } catch (error) {
      SentryAPI.trackError('Error fetching presigned URL', {
        server: true,
        networkId: network?.id || undefined,
        error,
      });
    }
  };

  return (
    <Formik
      initialValues={{ cvUrl: [], email: '', additionalInfo: '', termsAgreement: false, resumeUrl: '' }}
      validationSchema={formSchema}
    >
      {({ setFieldValue, errors, touched, setTouched }) => (
        <Form>
          <Flex
            data-testid="profile-form"
            flexDirection="column"
            sx={{ rowGap: 4, '& > div:not(:last-child)': { marginBottom: 0 }, 'label + div': { color: 'text.main' } }}
          >
            <Field
              type="email"
              name="email"
              id="email"
              component={FormInput}
              label="Email"
              labelExtra="Required"
              optional
              value={data.email}
              size="large"
              onChange={({ target }) => {
                onChange('profile', { ...data, email: target.value });
                setFieldValue('email', target.value);
              }}
            />

            <Field
              type="text"
              name="linkedinUrl"
              id="linkedin"
              component={FormInput}
              label="LinkedIn profile URL"
              placeholder="https://www.linkedin.com/in/..."
              labelExtra="Required"
              size="large"
              helpText={
                <Text opacity="0.9" fontSize="14px" color="text.main" as="p">
                  To find it,{' '}
                  <Text color="text.main" target="__blank" as="a" href="https://www.linkedin.com/in/">
                    click here
                  </Text>{' '}
                  and copy the URL from your browser.
                </Text>
              }
              optional
              value={data.linkedinUrl}
              onChange={({ target }) => {
                onChange('profile', { ...data, linkedinUrl: target.value });
                setFieldValue('linkedinUrl', target.value);
              }}
            />

            <Box>
              <Field
                component={FormFile}
                name="cvUrl"
                id="cvUrl"
                label="Resume"
                labelExtra="Optional"
                helpText="Strongly recommended – adding your resume will help you get more introductions."
                accept=".pdf, .doc, .docx"
                onChange={(e) => handleFileChange(e, setFieldValue)}
                field={{
                  id: 'cvUrl',
                  name: 'cvUrl',
                  value: data.cvUrl ? [data.cvUrl] : [],
                }}
                form={{
                  setFieldValue: (_, value) => {
                    onChange('profile', { ...data, cvUrl: value.pop() });
                  },
                }}
              />

              {resumeError && (
                <Flex
                  alignItems="center"
                  mt="16px"
                  sx={{ div: { margin: '0px', padding: '0px' } }}
                  minHeight="40px"
                  padding="0px 8px"
                  backgroundColor="red.20"
                >
                  <FormError>
                    Upload a .pdf, .doc or .docx file up to 10MB. The filename can only contain letters, numbers,
                    hyphens (-), and underscores (_), and must be between 1 and 255 characters long.
                  </FormError>
                </Flex>
              )}
            </Box>

            <Field
              type="text"
              id="additionalInfo"
              name="additionalInfo"
              component={FormInput}
              value={data.additionalInfo}
              label="Additional online presence"
              labelExtra="Optional"
              size="large"
              helpText="Share your personal website, portfolio, or a social profile."
              onChange={({ target }) => {
                onChange('profile', { ...data, additionalInfo: target.value });
                setFieldValue('additionalInfo', target.value);
              }}
            />

            <Field type="hidden" name="resumeUrl" id="resumeUrl" />

            <Box>
              <Flex alignItems="center">
                <Field
                  validate={(value) => (!value ? 'Accepting terms of service and privacy policy is required' : null)}
                  component={Checkbox}
                  variant={`${variant}Checkbox`}
                  id="termsAgreement"
                  name="termsAgreement"
                  checked={data.termsAgreement}
                  value={data.termsAgreement}
                  onChange={() => {
                    onChange('profile', { ...data, termsAgreement: !data.termsAgreement });
                    setTouched({ ...touched, termsAgreement: true });
                  }}
                />
                <Text
                  onClick={() => {
                    onChange('profile', { ...data, termsAgreement: !data.termsAgreement });
                    setTouched({ ...touched, termsAgreement: true });
                  }}
                  color="text.main"
                  sx={{ cursor: 'pointer', a: { color: 'text.main' } }}
                >
                  I have read and accept{` ${network.name}'s `}
                  <a onClick={(e) => e.stopPropagation()} href={privacyPolicyUrl} target="_blank">
                    Privacy Policy
                  </a>{' '}
                  and the{' '}
                  <a onClick={(e) => e.stopPropagation()} href="https://www.getro.com/terms" target="_blank">
                    Terms of Use
                  </a>{' '}
                  of this software.
                </Text>
              </Flex>
              {!data.termsAgreement && touched.termsAgreement && (
                <Flex
                  alignItems="center"
                  mt="16px"
                  sx={{ div: { margin: '0px', padding: '0px' } }}
                  minHeight="40px"
                  padding="0px 8px"
                  backgroundColor="red.20"
                >
                  <FormError>{errors.termsAgreement}</FormError>
                </Flex>
              )}
            </Box>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

Profile.propTypes = {
  network: NetworkSchema.isRequired,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  handleFileTooBig: PropTypes.func.isRequired,
  variant: PropTypes.string,
};

Profile.defaultProps = {
  variant: 'default',
};

export default Profile;
